/**
 * Verification policy: how stale a listing may be, and what each signal is
 * allowed to do about it.
 *
 * Isomorphic on purpose. The public "Verified N days ago" label and the task
 * that produces it read the same constants, so the site cannot claim a
 * freshness standard the pipeline does not meet.
 */

/**
 * Businesses that appear on a ranking page are re-verified far more often. A
 * dead listing at rank 3 is a credibility problem; the same listing at rank
 * 400 in a category nobody has built pages for is not worth the API call.
 */
export const RANKED_VERIFY_DAYS = 14
export const UNRANKED_VERIFY_DAYS = 90

/**
 * Consecutive failed website checks before the site is treated as dead.
 *
 * One failure is a bad minute on someone's host. Three sweeps apart is a
 * pattern.
 */
export const WEBSITE_FAILURE_THRESHOLD = 3

/** Request timeout for a website check. */
export const WEBSITE_TIMEOUT_MS = 10_000

/**
 * Google's operating status values that mean the business is gone for good.
 * "temporarily_closed" is deliberately absent: seasonal shutdowns and remodels
 * are not closures, and delisting on one would be wrong in a way that is
 * invisible to us and expensive for the business.
 */
export const PERMANENTLY_CLOSED_STATUSES = ['closed_forever'] as const

export function isPermanentlyClosed(gbpStatus: string | null | undefined) {
	if (!gbpStatus) return false
	return (PERMANENTLY_CLOSED_STATUSES as readonly string[]).includes(gbpStatus)
}

export function isTemporarilyClosed(gbpStatus: string | null | undefined) {
	return gbpStatus === 'temporarily_closed'
}

/** How old a verification may be before the business is due again. */
export function verifyIntervalDays(isRanked: boolean) {
	return isRanked ? RANKED_VERIFY_DAYS : UNRANKED_VERIFY_DAYS
}

/**
 * Confidence that the listing still reflects a real, operating business.
 *
 * Deliberately separate from scoreConfidence, which is about how much review
 * data backs the score. "Is this business real" and "is our score any good"
 * are different questions and one should never overwrite the other.
 */
export function verificationConfidence(input: {
	lastVerifiedAt: Date | null
	websiteFailures: number
	gbpStatus: string | null
	isRanked: boolean
	now?: Date
}): 'high' | 'medium' | 'low' {
	const now = input.now ?? new Date()

	if (!input.lastVerifiedAt) return 'low'
	if (isTemporarilyClosed(input.gbpStatus)) return 'low'
	if (input.websiteFailures >= WEBSITE_FAILURE_THRESHOLD) return 'medium'

	const ageDays = (now.getTime() - input.lastVerifiedAt.getTime()) / 86_400_000
	const interval = verifyIntervalDays(input.isRanked)

	if (ageDays <= interval) return 'high'
	if (ageDays <= interval * 2) return 'medium'
	return 'low'
}

/**
 * Human-readable freshness, rendered relatively so staleness is legible at a
 * glance instead of requiring the reader to do date arithmetic.
 *
 * Returns null when we have never verified. There is deliberately no fallback
 * wording like "recently": a date on the page is a promise, and inventing one
 * is worse than showing nothing.
 */
export function formatVerifiedAge(
	lastVerifiedAt: Date | string | null,
	now: Date = new Date(),
): string | null {
	if (!lastVerifiedAt) return null
	const date =
		lastVerifiedAt instanceof Date ? lastVerifiedAt : new Date(lastVerifiedAt)
	if (Number.isNaN(date.getTime())) return null

	const days = Math.floor((now.getTime() - date.getTime()) / 86_400_000)

	if (days <= 0) return 'today'
	if (days === 1) return 'yesterday'
	if (days < 30) return `${days} days ago`
	if (days < 60) return 'last month'
	const months = Math.floor(days / 30)
	if (months < 12) return `${months} months ago`
	return 'over a year ago'
}
